/**
 * Dropdown — Player
 */

import * as React from 'react';
import {
    cn,
    Button,
    type ElementPlayerProps,
    useIsMobile,
} from '@ai-content-hub/content-kit';
import {
    useLocale,
    type ContentConfig,
    CompositePlayerGroupedItemsBody,
    orderItemsByQuestionsSidebarRows,
    useCompositePlayerSidebarLayout,
    ContentPlayerSplitLayout,
} from '@ai-content-hub/content-core';
import map from 'lodash/map';
import trim from 'lodash/trim';
import { FITBPlayer, PagePlayer, defaultPageData, type FITBAnswer, type FITBElementData, RichTextInline } from '@ai-content-hub/content-elements';
import type { QtDropdownConfig, QtDropdownData, QtDropdownItem, QtDropdownScoringConfig } from './schema';
import type { QtDropdownLayoutConfig } from './schema';
import { normalizeQtDropdownAnswerMap } from './schema';
import { gradeQtDropdown, type QtDropdownItemResult } from './scorer';
import { normalizeQtDropdownLayoutConfig } from './layout';
import './qt-dropdown-player.css';

const EMPTY_ITEM_RESULTS: QtDropdownItemResult[] = [];

type NestedElementPlayerConfig = NonNullable<ElementPlayerProps['config']>;

function toNestedPlayerConfig(value: ContentConfig | undefined): NestedElementPlayerConfig {
    return value as unknown as NestedElementPlayerConfig;
}

function mergeDropdownFitbConfig(
    base: ContentConfig | undefined,
    parent?: QtDropdownConfig,
): NestedElementPlayerConfig {
    return toNestedPlayerConfig({
        ...(base ?? {}),
        blankMode: 'dropdown',
        shuffleOptions: parent?.shuffleOptions ?? true,
        caseSensitive: parent?.caseSensitive ?? false,
    } as ContentConfig);
}

function isFitbAnswered(data: FITBElementData, answer: unknown): boolean {
    const blanks = data.blanks ?? [];
    if (blanks.length === 0) return true;
    if (answer == null || typeof answer !== 'object') return false;
    const f = answer as FITBAnswer;
    return blanks.every((b) => {
        const v = f[b.id];
        return typeof v === 'string' && trim(v) !== '';
    });
}

function isItemAnswered(item: QtDropdownItem, raw: unknown): boolean {
    return isFitbAnswered(item.data, raw);
}


export interface DropdownPlayerProps {
    data: QtDropdownData;
    config?: QtDropdownConfig & {
        behavior?: {
            allowRetry?: boolean;
            showCorrectAnswer?: boolean;
            showExplanation?: boolean;
            instantFeedback?: boolean;
            examMode?: boolean;
        };
        scoring?: {
            max?: number;
            partialCreditEnabled?: boolean;
            passingScore?: number;
        };
    };
    onAnswer?: (answer: Record<string, unknown>) => void;
    showFeedback?: boolean;
    isReview?: boolean;
    disabled?: boolean;
    className?: string;
    answer?: Record<string, unknown>;
    result?: {
        isCorrect?: boolean | null;
        score?: number;
        max?: number;
        details?: Record<string, unknown>;
    };
}

export const DropdownPlayer: React.FC<DropdownPlayerProps> = ({
    data,
    config,
    onAnswer,
    answer: answerProp,
    result: resultProp,
    showFeedback: showFeedbackProp,
    isReview = false,
    disabled = false,
    className,
}) => {
    const questionOffset = (config as { questionOffset?: number } | undefined)?.questionOffset ?? 0;
    const scoringConfig = config?.scoring as QtDropdownScoringConfig | undefined;
    const scoringFingerprint = React.useMemo(
        () =>
            [
                scoringConfig?.partialCreditEnabled,
                scoringConfig?.pointsPerQuestion,
                scoringConfig?.passingThreshold,
                (scoringConfig as { max?: number } | undefined)?.max,
                (scoringConfig as { passingScore?: number } | undefined)?.passingScore,
            ].join('\0'),
        [
            scoringConfig?.partialCreditEnabled,
            scoringConfig?.pointsPerQuestion,
            scoringConfig?.passingThreshold,
            (scoringConfig as { max?: number } | undefined)?.max,
            (scoringConfig as { passingScore?: number } | undefined)?.passingScore,
        ],
    );
    const behaviorConfig = config?.behavior;
    const i18nConfig = config?.i18n;
    const { t } = useLocale({ config: i18nConfig });
    const isMobilePlayer = useIsMobile(768);

    const showCorrectAnswer = behaviorConfig?.showCorrectAnswer ?? true;
    const instantFeedback = behaviorConfig?.instantFeedback ?? false;
    const examMode = behaviorConfig?.examMode ?? false;

    const [answers, setAnswers] = React.useState<Record<string, unknown>>(() =>
        normalizeQtDropdownAnswerMap(
            data,
            answerProp && typeof answerProp === 'object' && !Array.isArray(answerProp)
                ? (answerProp as Record<string, unknown>)
                : {},
        ),
    );

    React.useEffect(() => {
        if (answerProp && typeof answerProp === 'object' && !Array.isArray(answerProp)) {
            const next = normalizeQtDropdownAnswerMap(data, answerProp as Record<string, unknown>);
            setAnswers((prev) => {
                const a = JSON.stringify(prev);
                const b = JSON.stringify(next);
                return a === b ? prev : next;
            });
        }
    }, [answerProp, data]);

    const [stepIndex, setStepIndex] = React.useState(0);
    const [visitedTabIndices, setVisitedTabIndices] = React.useState<Set<number>>(() => new Set());

    const items = data?.items ?? [];
    const itemIds = React.useMemo(() => map(items, (it) => it.id), [items]);
    const itemById = React.useMemo(
        () => new Map(map(items, (it) => [it.id, it] as const)),
        [items],
    );
    const { sidebarRows } = useCompositePlayerSidebarLayout(
        itemIds,
        data?.questionsSidebarRows,
    );
    const orderedItems = React.useMemo(
        () => orderItemsByQuestionsSidebarRows(items, sidebarRows),
        [items, sidebarRows],
    );
    const itemResultsRaw = resultProp?.details?.items;
    const itemResults = React.useMemo((): QtDropdownItemResult[] => {
        if (Array.isArray(itemResultsRaw) && itemResultsRaw.length > 0) {
            return itemResultsRaw as QtDropdownItemResult[];
        }
        const allowLocalGrade =
            (showFeedbackProp || isReview) && data != null && items.length > 0;
        if (!allowLocalGrade) {
            return EMPTY_ITEM_RESULTS;
        }
        try {
            const graded = gradeQtDropdown(data, answers as Record<string, unknown>, config);
            const itemsOut = graded.details?.items;
            if (Array.isArray(itemsOut) && itemsOut.length > 0) {
                return itemsOut as QtDropdownItemResult[];
            }
        } catch {
            return EMPTY_ITEM_RESULTS;
        }
        return EMPTY_ITEM_RESULTS;
    }, [
        itemResultsRaw,
        showFeedbackProp,
        isReview,
        items.length,
        data,
        answers,
        scoringFingerprint,
        config,
    ]);
    const submittedOrFeedback = Boolean(showFeedbackProp || isReview);
    // ELMS-2370: hide answer => no green/red grade colouring on the nav.
    const useGradeNavColors = submittedOrFeedback && showCorrectAnswer && itemResults.length > 0;

    React.useEffect(() => {
        if (orderedItems.length === 0) {
            setStepIndex(0);
            return;
        }
        setStepIndex((i) => Math.min(Math.max(0, i), orderedItems.length - 1));
    }, [orderedItems.length]);

    React.useEffect(() => {
        if (orderedItems.length === 0) {
            return;
        }
        setVisitedTabIndices((prev) => {
            const next = new Set<number>();
            prev.forEach((i) => {
                if (i >= 0 && i < orderedItems.length) {
                    next.add(i);
                }
            });
            next.add(stepIndex);
            return next;
        });
    }, [stepIndex, orderedItems.length]);

    const layoutCfg = normalizeQtDropdownLayoutConfig(
        config?.layout as QtDropdownLayoutConfig | undefined,
    );
    const layoutType = layoutCfg.type;
    const splitRatio = layoutCfg?.splitRatio;
    const displayModeRaw = layoutCfg?.displayMode ?? 'all';
    const displayMode = displayModeRaw === 'step' ? 'tabs' : displayModeRaw;

    const pageForPlayer = React.useMemo(() => {
        if (!data) {
            return null;
        }
        return {
            ...defaultPageData,
            ...data.pageContent,
            sections: data.pageContent?.sections ?? defaultPageData.sections,
        };
    }, [data]);

    const hasPageContent = (pageForPlayer?.sections?.length ?? 0) > 0;
    const panelHorizontal = hasPageContent && layoutType === 'horizontal' && !isMobilePlayer;

    if (!data) {
        return (
            <div
                className={cn(
                    'qt-dropdown-player qt-dropdown-player--empty text-muted-foreground italic p-4',
                    className,
                )}
            >
                {t('noQuestions', 'No questions available')}
            </div>
        );
    }

    const handleItemAnswer = (itemId: string, itemAnswer: unknown) => {
        if (disabled) return;
        const next = { ...answers, [itemId]: itemAnswer };
        setAnswers(next);
        onAnswer?.(next);
    };

    const fitbPlayerConfig = mergeDropdownFitbConfig(config, config as QtDropdownConfig | undefined);

    // Build elementChildLabels per item: blanks are numbered sequentially
    // across all items (e.g. item 1 blanks → 1,2; item 2 blanks → 3,4).
    const totalBlanks = React.useMemo(
        () => items.reduce((sum, it) => sum + (it.data.blanks?.length ?? 0), 0),
        [items],
    );
    const showBlankLabels = totalBlanks > 1;
    const childLabelsPerItem = React.useMemo(() => {
        if (!showBlankLabels) return new Map<string, { fitbBlank: Record<string, number> }>();
        const labelsMap = new Map<string, { fitbBlank: Record<string, number> }>();
        let runningN = 1;
        for (const it of items) {
            const blanks = it.data.blanks ?? [];
            const fitbBlank: Record<string, number> = {};
            for (let i = 0; i < blanks.length; i++) {
                fitbBlank[`blank-${i}`] = runningN++;
            }
            labelsMap.set(it.id, { fitbBlank });
        }
        return labelsMap;
    }, [items, showBlankLabels]);

    const renderQuestion = (item: QtDropdownItem) => {
        const answer = answers[item.id];
        const itemResult = itemResults.find((r) => r.itemId === item.id);
        const resultForPlayer =
            itemResult != null
                ? {
                      isCorrect: itemResult.isCorrect,
                      score: itemResult.score,
                      max: itemResult.max,
                      details: itemResult.details,
                  }
                : undefined;

        const showFb = examMode
            ? Boolean(showFeedbackProp)
            : instantFeedback
              ? isFitbAnswered(item.data, answer)
              : Boolean(showFeedbackProp);

        return (
            <div
                key={item.id}
                className="qt-dropdown-player-item qt-dropdown-player-item--fitb scroll-mt-6 rounded-md"
            >
                <FITBPlayer
                    data={item.data}
                    answer={(answer as FITBAnswer) ?? {}}
                    onAnswer={(ans) => handleItemAnswer(item.id, ans)}
                    disabled={disabled}
                    showFeedback={showFb && showCorrectAnswer}
                    result={resultForPlayer}
                    isReview={isReview}
                    config={fitbPlayerConfig}
                    elementChildLabels={childLabelsPerItem.get(item.id)}
                />
            </div>
        );
    };

    const pageBlock =
        hasPageContent && pageForPlayer ? (
            <div className="qt-dropdown-player-page">
                <PagePlayer
                    data={pageForPlayer}
                    submitted={submittedOrFeedback}
                    className="qt-dropdown-player-page-inner"
                />
            </div>
        ) : null;

    const emptyQuestionsMessage =
        items.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
                {t('noQuestions', 'No questions available')}
            </p>
        ) : null;

    let questionsBody: React.ReactNode = null;
    if (items.length > 0) {
        if (displayMode === 'all') {
            questionsBody = (
                <CompositePlayerGroupedItemsBody
                    prefix="qt-dropdown-player"
                    itemIds={itemIds}
                    questionsSidebarRows={data.questionsSidebarRows}
                    renderItem={(id) => {
                        const item = itemById.get(id);
                        return item ? renderQuestion(item) : null;
                    }}
                />
            );
        } else {
            questionsBody = (
                <div className="qt-dropdown-player-questions qt-dropdown-player-questions--tabs flex flex-col gap-6">
                    {submittedOrFeedback ? (
                        <div className="qt-dropdown-player-questions qt-dropdown-player-questions--tabs-review space-y-8">
                            {map(orderedItems, (item) => renderQuestion(item))}
                        </div>
                    ) : (
                        <>
                            {orderedItems[stepIndex] ? renderQuestion(orderedItems[stepIndex]) : null}
                            {orderedItems.length > 1 ? (
                                <div
                                    className="qt-dropdown-player-tab-row flex flex-wrap justify-center gap-2 border-t border-border pt-4"
                                    role="tablist"
                                >
                                    {map(orderedItems, (item, idx) => {
                                        const itemResult = itemResults.find((r) => r.itemId === item.id);
                                        const isCurrent = idx === stepIndex;
                                        const answered = isItemAnswered(item, answers[item.id]);
                                        const visited = visitedTabIndices.has(idx);
                                        let tabClass =
                                            'qt-dropdown-player-tab cursor-pointer min-w-9 transition-colors duration-200 border-border text-muted-foreground shadow-none hover:!bg-muted/40';
                                        if (useGradeNavColors) {
                                            if (isCurrent) {
                                                tabClass = cn(
                                                    tabClass,
                                                    'ring-2 ring-offset-2 ring-offset-background',
                                                    itemResult?.isCorrect === true
                                                        ? 'ring-success/50 !border-success/80 !bg-success/10'
                                                        : 'ring-destructive/50 !border-destructive/80 !bg-destructive/10',
                                                );
                                            } else if (itemResult?.isCorrect === true) {
                                                tabClass =
                                                    'qt-dropdown-player-tab !border-success/60 !bg-success/15 text-foreground shadow-none hover:!bg-success/25';
                                            } else {
                                                tabClass =
                                                    'qt-dropdown-player-tab !border-destructive/60 !bg-destructive/15 text-foreground shadow-none hover:!bg-destructive/25';
                                            }
                                        } else if (isCurrent) {
                                            tabClass = cn(
                                                tabClass,
                                                'ring-2 ring-primary/40 ring-offset-2 ring-offset-background !bg-primary/10 text-foreground',
                                            );
                                        } else if (answered) {
                                            tabClass =
                                                'qt-dropdown-player-tab !border-success/60 !bg-success/15 text-foreground';
                                        } else if (visited) {
                                            tabClass =
                                                'qt-dropdown-player-tab !border-warning/60 !bg-warning/15 text-foreground';
                                        }
                                        return (
                                            <Button
                                                key={item.id}
                                                type="button"
                                                size="sm"
                                                variant="outline"
                                                className={tabClass}
                                                aria-selected={isCurrent}
                                                onClick={() => setStepIndex(idx)}
                                                disabled={disabled}
                                            >
                                                {questionOffset + idx + 1}
                                            </Button>
                                        );
                                    })}
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            );
        }
    }

    const questionsScrollableBlock = (
        <>
            {emptyQuestionsMessage}
            {questionsBody}
        </>
    );

    const questionsColumn = (
        <div
            className={cn(
                'qt-dropdown-player-questions-column min-w-0 flex flex-col',
                panelHorizontal && 'min-h-0 flex-1',
            )}
        >
            {panelHorizontal ? (
                <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain border-l border-border pl-4 lg:pl-6">
                    <div className="space-y-6 pr-1">{questionsScrollableBlock}</div>
                </div>
            ) : (
                <div className="space-y-6">{questionsScrollableBlock}</div>
            )}
        </div>
    );

    let mainBody: React.ReactNode;
    if (panelHorizontal) {
        mainBody = (
            <ContentPlayerSplitLayout
                prefix="qt-dropdown-player-split"
                splitRatio={splitRatio}
                leftPanel={
                    <div className="qt-dropdown-player-split-page min-w-0 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain pr-1">
                        {pageBlock}
                    </div>
                }
                rightPanel={
                    <div className="qt-dropdown-player-split-questions min-w-0 min-h-0 flex flex-col overflow-hidden">
                        {questionsColumn}
                    </div>
                }
            />
        );
    } else {
        mainBody = (
            <>
                {pageBlock}
                {questionsColumn}
            </>
        );
    }

    return (
        <div
            className={cn(
                'qt-dropdown-player',
                panelHorizontal
                    ? 'qt-dropdown-player--horizontal-split flex flex-col min-h-0 flex-1 gap-6'
                    : 'space-y-6',
                className,
            )}
        >
            {data.instructions ? (
                <div className="qt-dropdown-player-header flex-shrink-0 space-y-2">
                    <RichTextInline text={data.instructions} as="p" className="text-sm text-muted-foreground" />
                </div>
            ) : null}

            {mainBody}
        </div>
    );
};

export default DropdownPlayer;
